import dayjs from 'dayjs'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector, shallowEqual, useDispatch } from 'react-redux'
import { Card, CardBody, Button } from 'reactstrap'

import { selectCsvUploaderStatus, getBuckets, deleteWorkResults } from 'slices/csvUploaderSlice'
import { showError, showSuccess } from 'slices/notificationSlice'
import { ENABLE_DIALOG_ERROR_STATUS_CODES, ERROR_STATUS_CODE } from 'slices/utils'

import { CardSubmitFooter, SelectBoxFormat, SingleDateRangePicker } from 'components/common'
import { COLUMN_SIZES } from 'components/common/constants'

import useBusinessTime from 'hooks/useBusinessTime'

import { CsvAutoRecordsDeleteConfirmDialog } from './CsvAutoRecordsDeleteConfirmDialog'

const MAX_DAYS_AGO = 45
const MAX_RANGE_DAYS = 31

export const CsvAutoRecordsDeleteTab = () => {
  const [selectedBucketDirectory, setSelectedBucketDirectory] = useState<string>()
  const [period, setPeriod] = useState<{ start: Date; end: Date }>()
  const [openRangeDatePicker, setOpenRangeDatePicker] = useState(false)
  const [isConfirmDialogOpen, setIsConfirmDialogOpen] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [isConflict, setIsConflict] = useState(false)

  const dispatch = useDispatch()
  const { buckets, isRequesting, errorMessage } = useSelector(selectCsvUploaderStatus, shallowEqual)
  const { getWorkDate } = useBusinessTime()

  const bucketItems = useMemo(() => buckets.map(b => ({ key: b.bucketDirectory, value: b.name })), [buckets])

  const unchanged = !selectedBucketDirectory && !period
  const disabled = !selectedBucketDirectory || !period

  useEffect(() => {
    dispatch(getBuckets())
  }, [dispatch])

  useEffect(() => {
    if (!submitted || isRequesting) {
      return
    }
    if (errorMessage === '') {
      dispatch(showSuccess({ successMessage: '個人別作業実績数の削除に成功しました。' }))
      setSelectedBucketDirectory(undefined)
      setPeriod(undefined)
      setIsConfirmDialogOpen(false)
    } else if (errorMessage === ERROR_STATUS_CODE.CONFLICT) {
      // 409のときはダイアログを閉じず、同じダイアログ内で集計中の旨を伝える
      setIsConflict(true)
    } else {
      setIsConfirmDialogOpen(false)
      if (!ENABLE_DIALOG_ERROR_STATUS_CODES.includes(errorMessage)) {
        dispatch(showError({ errorMessage: '削除できませんでした。' }))
      }
    }
    setSubmitted(false)
  }, [submitted, isRequesting, errorMessage, dispatch])

  const onCancel = useCallback(() => {
    setSelectedBucketDirectory(undefined)
    setPeriod(undefined)
  }, [])

  const handleDeleteApprove = useCallback(() => {
    if (!selectedBucketDirectory || !period) {
      return
    }
    dispatch(
      deleteWorkResults(
        selectedBucketDirectory,
        dayjs(period.start).format('YYYY-MM-DD'),
        dayjs(period.end).format('YYYY-MM-DD')
      )
    )
    setSubmitted(true)
  }, [dispatch, selectedBucketDirectory, period])

  return (
    <div className="mx-3">
      <Card>
        <CardBody className="p-4">
          <div className="d-flex flex-column row-gap-4">
            <div>
              <div className="mb-3">個人別作業実績数を一括で削除できる機能です。</div>

              <div className="my-3">
                <div className="my-2 fw-bold font-middle">基本事項</div>
                <ul className="mb-3">
                  <li>この機能を利用するためには事前設定が必要になるため、営業担当者までご連絡ください。</li>
                  <li>
                    削除の対象となるのは、「作業管理」で実績入力方法が「自動アップロード」となっている作業のみです。
                  </li>
                </ul>
              </div>

              <div className="my-3">
                <div className="my-2 fw-bold font-middle">削除に関する注意事項</div>
                <ul className="mb-3">
                  <li>削除は選択したデータベースの指定した期間（営業日単位）を対象に実行されます。</li>
                  <li>
                    一つのデータベースが複数の作業から参照されている場合、参照されている作業全ての個人別作業実績数が削除されます。ご注意ください。
                  </li>
                  <li>当日を除く45営業日前までの削除が可能です。それより以前の個人別作業実績数は削除できません。</li>
                  <li>
                    個人別作業実績数のアップロードに伴う集計処理が行われている間は削除できません。時間をおいて再度実施していただくか、CSVアップローダーを一時停止した上で再度実施してください。
                  </li>
                  <ul>
                    <li>
                      個人別作業実績数のアップロードに伴う集計処理が発生する操作 :
                      CSVアップローダーからのアップロード、個人別作業実績数アップロード機能からのアップロード
                    </li>
                    <li>大きなファイルをアップロードしている場合、集計処理完了までに1時間程度かかることがあります。</li>
                  </ul>
                </ul>
              </div>
            </div>
            <SelectBoxFormat
              label="データベース"
              placeholder="データベースを選択"
              value={selectedBucketDirectory}
              size={COLUMN_SIZES.MIDDLE}
              items={bucketItems}
              onChange={item => setSelectedBucketDirectory(item.key?.toString())}
            />
            <div>
              <div className="my-2 fw-bold font-middle">削除対象期間</div>
              <div className="mb-2">最大31日分の一括削除が可能です。</div>
              <Button outline onClick={() => setOpenRangeDatePicker(true)}>
                {period
                  ? `${dayjs(period.start).format('YYYY/MM/DD')} - ${dayjs(period.end).format('YYYY/MM/DD')}`
                  : '期間を選択'}
              </Button>
              <SingleDateRangePicker
                isOpen={openRangeDatePicker}
                from={period?.start}
                to={period?.end}
                maxRange={MAX_RANGE_DAYS}
                minDate={dayjs(getWorkDate(dayjs().format('YYYY-MM-DD')))
                  .subtract(MAX_DAYS_AGO, 'day')
                  .toDate()}
                maxDate={dayjs(getWorkDate(dayjs().format('YYYY-MM-DD')))
                  .subtract(1, 'day')
                  .toDate()}
                onCancel={() => setOpenRangeDatePicker(false)}
                onChange={(start, end) => {
                  setPeriod({ start, end })
                  setOpenRangeDatePicker(false)
                }}
              />
            </div>
          </div>
        </CardBody>
        <CardSubmitFooter
          onCancel={onCancel}
          onSubmit={() => setIsConfirmDialogOpen(true)}
          submitDisabled={disabled || unchanged}
          cancelDisabled={unchanged}
          submitLabel="削除"
          submitColor="danger"
          submitName="csv-auto-records-delete-submit"
        />
      </Card>
      <CsvAutoRecordsDeleteConfirmDialog
        isOpen={isConfirmDialogOpen}
        isConflict={isConflict}
        onApprove={handleDeleteApprove}
        onCancel={() => {
          setIsConfirmDialogOpen(false)
          setIsConflict(false)
        }}
      />
    </div>
  )
}
